/**
 * Радар рынка — общий словарь сервера и терминала.
 *
 * Сервер следит за всем рынком фьючерсов Binance: живой ценой каждой
 * монеты, объёмом и перевесом покупок по минутам, открытым интересом,
 * фандингом и ликвидациями. Из этого складываются строки радара
 * (`RadarRow`) и события (`RadarEvent`) — «памп», «шорт-сквиз»,
 * «объём без движения». Каждое событие сервер потом проверяет: что
 * было через 15 минут, час и 4 часа, — и честно копит статистику.
 *
 * Свой радар человека (`UserRadar`) — фильтр по тем же числам или по
 * типам событий. Хранится в профиле; сервер проверяет его сам и шлёт
 * оповещение, даже когда вкладка закрыта.
 */
export const RADAR_EVENTS = {
    pump: { title: 'Памп', hint: 'Резкий рост за минуты — заметно сильнее обычного для этой монеты.', direction: 'up', family: 'move' },
    dump: { title: 'Дамп', hint: 'Резкое падение за минуты — заметно сильнее обычного для этой монеты.', direction: 'down', family: 'move' },
    'short-squeeze': {
        title: 'Шорт-сквиз',
        hint: 'Рост, который толкают вынужденные покупки: шорты ликвидирует, открытый интерес падает. Часто выдыхается быстро.',
        direction: 'up',
        family: 'move',
    },
    'long-squeeze': {
        title: 'Лонг-сквиз',
        hint: 'Падение, которое толкают вынужденные продажи: лонги ликвидирует, открытый интерес падает.',
        direction: 'down',
        family: 'move',
    },
    'leverage-pump': {
        title: 'Разгон плечом',
        hint: 'Рост на фьючерсах с притоком новых позиций: открытый интерес растёт, спот почти не участвует. Держится, пока плечо не сбросят.',
        direction: 'up',
        family: 'move',
    },
    'leverage-dump': {
        title: 'Пролив плечом',
        hint: 'Падение с притоком новых шортов: открытый интерес растёт на снижении.',
        direction: 'down',
        family: 'move',
    },
    'spot-pump': {
        title: 'Памп на споте',
        hint: 'Рост с живыми покупками на споте — спрос настоящий, а не только плечо.',
        direction: 'up',
        family: 'move',
    },
    'spot-dump': { title: 'Продажи на споте', hint: 'Падение, которое ведут продажи на споте: монету реально сбрасывают.', direction: 'down', family: 'move' },
    'liq-cascade': {
        title: 'Каскад ликвидаций',
        hint: 'За минуты ликвидировало крупную сумму позиций: цену двигают вынужденные сделки.',
        direction: 'none',
        family: 'flow',
    },
    absorption: {
        title: 'Объём без движения',
        hint: 'Объём в разы выше обычного, а цена стоит: кто-то крупный набирает или раздаёт позицию.',
        direction: 'none',
        family: 'flow',
    },
    accumulation: {
        title: 'Тихий набор',
        hint: 'Цена почти не двигается, а покупки по рынку заметно перевешивают продажи.',
        direction: 'up',
        family: 'flow',
    },
    distribution: { title: 'Тихая раздача', hint: 'Цена стоит, а продажи по рынку заметно перевешивают покупки.', direction: 'down', family: 'flow' },
    'breakout-high': { title: 'Пробой вверх', hint: 'Цена вышла выше максимума последних 4 часов с объёмом.', direction: 'up', family: 'level' },
    'breakout-low': { title: 'Пробой вниз', hint: 'Цена ушла ниже минимума последних 4 часов с объёмом.', direction: 'down', family: 'level' },
    'day-high': { title: 'Максимум суток', hint: 'Цена обновила максимум за 24 часа.', direction: 'up', family: 'level' },
    'day-low': { title: 'Минимум суток', hint: 'Цена обновила минимум за 24 часа.', direction: 'down', family: 'level' },
    'funding-long': {
        title: 'Толпа в лонгах',
        hint: 'Фандинг сильно положительный: лонги платят шортам. Перекос толпы — топливо для отката вниз.',
        direction: 'down',
        family: 'crowd',
    },
    'funding-short': {
        title: 'Толпа в шортах',
        hint: 'Фандинг сильно отрицательный: шорты платят лонгам. Топливо для сквиза вверх.',
        direction: 'up',
        family: 'crowd',
    },
    'strong-vs-btc': { title: 'Сильнее BTC', hint: 'Монета растёт, когда биткоин стоит или падает: у неё свой спрос.', direction: 'up', family: 'relative' },
    'weak-vs-btc': { title: 'Слабее BTC', hint: 'Монета падает, когда биткоин стоит или растёт: её продают отдельно.', direction: 'down', family: 'relative' },
};
export const RADAR_EVENT_TYPES = Object.keys(RADAR_EVENTS);
export const RADAR_METRICS = [
    { id: 'ch1m', label: 'Цена за 1 минуту', unit: '%', hint: 'Изменение цены за последнюю минуту.' },
    { id: 'ch5m', label: 'Цена за 5 минут', unit: '%', hint: 'Изменение цены за 5 минут.' },
    { id: 'ch15m', label: 'Цена за 15 минут', unit: '%', hint: 'Изменение цены за 15 минут.' },
    { id: 'ch1h', label: 'Цена за час', unit: '%', hint: 'Изменение цены за час.' },
    { id: 'ch24h', label: 'Цена за сутки', unit: '%', hint: 'Изменение цены за 24 часа.' },
    { id: 'vol5mX', label: 'Объём к обычному', unit: '×', hint: 'Объём последних 5 минут во сколько раз выше обычного.' },
    { id: 'volume24h', label: 'Оборот за сутки', unit: '$', hint: 'Сколько торговали за 24 часа.' },
    { id: 'cvd15m', label: 'Перевес покупок, 15 мин', unit: 'доля', hint: 'От −1 (только продажи) до +1 (только покупки) на фьючерсах.' },
    { id: 'cvd1h', label: 'Перевес покупок, час', unit: 'доля', hint: 'То же за час.' },
    { id: 'spotShare15m', label: 'Доля спота', unit: 'доля', hint: 'Какая часть оборота — на споте: 0 — только фьючерсы.' },
    { id: 'spotCvd15m', label: 'Перевес покупок на споте', unit: 'доля', hint: 'Кто перевешивает на споте за 15 минут.' },
    { id: 'oiUsd', label: 'Открытый интерес', unit: '$', hint: 'Сколько денег в открытых позициях.' },
    { id: 'oiCh15m', label: 'Открытый интерес за 15 минут', unit: '%', hint: 'Растёт — открывают новые позиции, падает — закрывают.' },
    { id: 'oiCh1h', label: 'Открытый интерес за час', unit: '%', hint: 'То же за час.' },
    { id: 'funding', label: 'Фандинг', unit: '%', hint: 'Плюс — лонги платят шортам, минус — наоборот.' },
    { id: 'liqLong15m', label: 'Ликвидации лонгов, 15 мин', unit: '$', hint: 'Сколько длинных позиций вынесло.' },
    { id: 'liqShort15m', label: 'Ликвидации шортов, 15 мин', unit: '$', hint: 'Сколько коротких позиций вынесло.' },
    { id: 'vsBtc15m', label: 'Сильнее BTC за 15 минут', unit: 'п.п.', hint: 'Насколько монета выросла больше биткоина.' },
];
/** Монета подходит под условия радара. Пустое число условию не отвечает. */
/** Условия на саму цену: по ним видно, рост ловит радар или падение. */
const PRICE_METRICS = new Set(['ch1m', 'ch5m', 'ch15m', 'ch1h', 'ch24h', 'vsBtc15m']);
/**
 * Куда смотрит радар по своим условиям: «цена выросла» — лонг, «упала» —
 * шорт. Нет условия на цену или оно в обе стороны («цена стоит») —
 * `null`: направление радар не задаёт.
 */
export function radarDirection(filters) {
    let up = false;
    let down = false;
    for (const filter of filters) {
        if (!PRICE_METRICS.has(filter.metric))
            continue;
        if (filter.op === 'above' && filter.value > 0)
            up = true;
        if (filter.op === 'below' && filter.value < 0)
            down = true;
    }
    return up === down ? null : up ? 'LONG' : 'SHORT';
}
export function radarMatches(row, filters) {
    if (filters.length === 0)
        return false;
    return filters.every((filter) => {
        const value = row[filter.metric];
        if (value === null || value === undefined || !Number.isFinite(value))
            return false;
        return filter.op === 'above' ? value > filter.value : value < filter.value;
    });
}
const METRIC_IDS = new Set(RADAR_METRICS.map((metric) => metric.id));
const EVENT_IDS = new Set(RADAR_EVENT_TYPES);
/** Радары из профиля: непонятная запись пропускается, а не ломает остальные. */
export function userRadarsFrom(raw) {
    if (!Array.isArray(raw))
        return [];
    const out = [];
    for (const entry of raw) {
        if (typeof entry !== 'object' || entry === null)
            continue;
        const value = entry;
        if (typeof value['id'] !== 'string' || value['id'] === '' || typeof value['name'] !== 'string')
            continue;
        const filters = Array.isArray(value['filters'])
            ? value['filters'].flatMap((item) => {
                if (typeof item !== 'object' || item === null)
                    return [];
                const filter = item;
                if (typeof filter['metric'] !== 'string' || !METRIC_IDS.has(filter['metric']))
                    return [];
                if (filter['op'] !== 'above' && filter['op'] !== 'below')
                    return [];
                if (typeof filter['value'] !== 'number' || !Number.isFinite(filter['value']))
                    return [];
                return [{ metric: filter['metric'], op: filter['op'], value: filter['value'] }];
            })
            : [];
        const events = Array.isArray(value['events']) ? value['events'].filter((id) => typeof id === 'string' && EVENT_IDS.has(id)) : [];
        if (filters.length === 0 && events.length === 0)
            continue;
        out.push({
            id: value['id'],
            name: value['name'].slice(0, 80),
            filters: filters.slice(0, 12),
            events: [...new Set(events)],
            notify: value['notify'] === true,
            ...(value['muted'] === true ? { muted: true } : {}),
            createdAt: typeof value['createdAt'] === 'number' ? value['createdAt'] : 0,
            updatedAt: typeof value['updatedAt'] === 'number' ? value['updatedAt'] : 0,
        });
    }
    return out.slice(0, 50);
}
//# sourceMappingURL=radar.js.map